import { Dispatch, FC, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Group, Stack, Tabs } from '@mantine/core';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { useListState, UseListStateHandlers } from '@mantine/hooks';
import { IconEdit, IconTrash } from '@tabler/icons';
import PanePaper from '../../../Molecules/PanePaper';
import { useModalState } from '../../../Molecules/Dialogs';
import MessageTemplateNameModal from '../../../Organismns/Settings/MessageTemplates/MessageTemplateNameModal';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { RoutingS } from '../../../Service/RoutingS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ConstantS } from '../../../Service/ConstantS';
import { MessageTemplateDto } from '../../../Types/MessageT';
import { MessageS } from '../../../Service/MessageS';
import { openDeleteConfirmModal } from '../../../Atoms/DeleteConfirmModal';
import EmptyText from '../../../Atoms/EmptyText';
import { createMessageTemplate } from '../../../Service/restapi/messageService';
import MessageTemplateListItem from './MessageTemplateListItem';
import { openRenameGroupModal } from '../../../Organismns/Message/MessageModals';

const MessageTemplateList = () => {
  const [messageTemplates, handlers] = useListState<MessageTemplateDto>([]);
  const { setState: setMessageTemplates } = handlers;
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { show, toggle } = useModalState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const existingNames = useMemo(
    () => messageTemplates.filter((m) => m.messageType === 'CUSTOM').map((m) => m.name),
    [messageTemplates],
  );

  const [activeTab, setActiveTab] = useState<string | null>('Allgemein');
  const groups = useMemo(
    () =>
      Array.from(new Set(messageTemplates.map((t) => t.groupName ?? 'Allgemein'))).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [messageTemplates],
  );

  const create = (templateName: string, groupName: string | null) => {
    createMessageTemplate('CUSTOM', templateName, groupName).then((resp) => {
      if (resp.ok) {
        MixpanelS.track(ConstantS.TrackingEvents.EmailTemplateCreated);
        navigate(`/settings/template/message/custom/${templateName}`);
      }
    });
  };

  const removeTemplate = (id: number) => setMessageTemplates(messageTemplates.filter((t) => t.id !== id));

  const onUpdate = (template: MessageTemplateDto) => {
    const tmp = messageTemplates.map((t) => (t.id === template.id ? template : t));
    setMessageTemplates(_.orderBy(tmp, ['name']));
  };

  useEffect(() => {
    RoutingS.changeTitle('Email-Vorlagen');
    setLoading(true);
    MessageS.fetchTemplates()
      .then((templates) => {
        MessageS.fillTemplateNames(templates, i18n);
        setMessageTemplates(_.orderBy(templates, ['name']));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <PanePaper
      title="message.templates"
      tooltip="help.settings.message.templates.pane"
      rightSection={
        <BookitupAddIcon
          onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.MULTIPLE_EMAIL_TEMPLATE, toggle)}
        />
      }
      sx={{ maxWidth: 500 }}
    >
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          {groups.map((groupName) => (
            <Tabs.Tab key={groupName} value={groupName}>
              {groupName}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {groups.map((groupName) => (
          <Tabs.Panel key={groupName} value={groupName} pt="xl">
            <GroupTemplates
              existingNames={existingNames}
              messages={messageTemplates}
              groupName={groupName === 'Allgemein' ? null : groupName}
              handlers={handlers}
              groups={groups}
              onDelete={(id: number) => removeTemplate(id)}
              setTab={setActiveTab}
              onUpdate={onUpdate}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
      <MessageTemplateNameModal show={show} toggle={toggle} finish={create} existingGroups={groups} />
    </PanePaper>
  );
};

interface Props {
  messages: MessageTemplateDto[];
  groupName: string | null;
  handlers: UseListStateHandlers<MessageTemplateDto>;
  groups: string[];
  setTab: Dispatch<string>;
  existingNames: string[];
  onUpdate: (template: MessageTemplateDto) => void;
  onDelete: (id: number) => void;
}

const GroupTemplates: FC<Props> = ({
  messages,
  groupName,
  handlers,
  groups,
  setTab,
  existingNames,
  onUpdate,
  onDelete,
}) => {
  const filtered = messages.filter((t) => t.groupName === groupName);
  const { applyWhere } = handlers;

  const rename = (name: string | null) => {
    applyWhere(
      (t) => t.groupName === groupName,
      (t) => ({ ...t, groupName: name }),
    );
    setTab(name === null ? 'Allgemein' : name);
  };

  const deleteGroup = () => {
    if (groupName) {
      const onConfirm = async () => {
        const deleted = await MessageS.renameGroup(groupName, null);
        if (deleted) {
          rename(null);
        }
        return deleted;
      };

      openDeleteConfirmModal({
        title: 'Gruppe löschen?',
        description: 'Möchtest Du die Gruppe wirklich löschen?',
        onConfirm,
      });
    }
  };

  return (
    <>
      {filtered.length === 0 && <EmptyText />}
      <Stack>
        {filtered.map((template) => (
          <MessageTemplateListItem
            key={template.id}
            template={template}
            onDelete={() => onDelete(template.id)}
            onUpdate={onUpdate}
            existingNames={existingNames}
            groups={groups}
            setTab={setTab}
          />
        ))}
      </Stack>
      {groupName && (
        <Group position="center" mt="xl">
          <Button
            size="xs"
            variant="subtle"
            leftIcon={<IconEdit size={16} />}
            onClick={() =>
              openRenameGroupModal({ initialValue: groupName, existingCategories: groups, onSuccess: rename })
            }
          >
            Gruppe bearbeiten
          </Button>
          <Button size="xs" color="red" variant="subtle" leftIcon={<IconTrash size={16} />} onClick={deleteGroup}>
            {i18n._('actions.delete')}
          </Button>
        </Group>
      )}
    </>
  );
};

export default MessageTemplateList;
